/**
 * tanstack query用に特定のAPIエンドポイントに応じた関数群を返すhooks
 * APIに応じてstaleTimeも設定する
 * */

import { useApi } from '@/core/api/useApi'
import { useCampaignSlug } from '@/core/app/useCampaignSlug'
import { useLocale } from '@/core/i18n/useLocale'
import type {
  CampaignGetResponse,
  MenuListResponse,
  NotificationGetResponse,
  NotificationListResponse,
  NotificationsUnreadResponse,
  QuestionnaireGetResponse,
  QuestionnaireListResponse,
  RewardGetResponse,
  RewardListResponse,
  SheetListResponse,
  SpotGetResponse,
  SpotListResponse,
  StampGetResponse,
  StampQuizListResponse,
  UserGetResponse,
  UserRewardStatusGetResponse,
  UserRewardStatusListResponse,
  UserSheetListResponse,
  UserStampListResponse,
} from '@/schemas/v2'
import { useTokenStore } from '@/store/useTokenStore'

interface FetchFn<T> {
  queryKey: string[]
  staleTime: number
  queryFn: () => Promise<T | undefined>
}

/* キャッシュをするAPIに付与するデフォルトのキャッシュ時間。1日に設定している */
const ONE_DAY_CACHE_TIME = 24 * 60 * 60 * 1000

/**
 * queryKey に載せる現在のロケールを返す（#3484 / #3529）。
 *
 * API のロケールは `X-Local` ヘッダー（`useCampaignHeaders`）で決まるため、
 * キーに含めないと「英語で取得したスポット詳細が中国語に切り替えても残り続ける」
 * という不具合になる（永続キャッシュがリロードを跨いで復元されるため）。
 *
 * `useLocale()` と同じ値を使うことで X-Local と queryKey のロケールが必ず一致する。
 * 各 FetchFn が個別に localStorage を読まないよう、参照はこのフックに一本化すること。
 */
export const useQueryLocale = (): string => useLocale().locale

/**
 * locale 込みの queryKey を組み立てる（#3529）。
 *
 * 位置は `[domain, campaignSlug, locale, ...rest]` に固定する。
 * `domain` / `campaignSlug` を先頭に置いたままにすることで、
 * 既存の前方一致 `invalidateQueries({ queryKey: ['spots'] })` などをそのまま活かせる。
 * `PERSIST_QUERY_KEYS`（pages/_app.tsx）も `queryKey[0]` で判定しているため影響しない。
 *
 * ユニットテストから検証するため export している。
 */
export const buildLocaleQueryKey = (
  domain: string,
  campaignSlug: string,
  locale: string,
  ...rest: Array<string | null | undefined>
): string[] => [domain, campaignSlug, locale, ...rest.filter((v): v is string => v != null && v !== '')]

/**
 * バックエンド master_update_timestamps のテーブル名 → TanStack Query の queryKey[0] マッピング。
 * GET /api/v2/master/update/check で true が返ったテーブルに対応するクエリを invalidate する。
 */
export const MASTER_TABLE_TO_QUERY_KEYS: Record<string, string[]> = {
  campaigns: ['campaign'],
  sheets: ['sheets'],
  menus: ['menus'],
  spots: ['spots'],
  notifications: ['notifications'],
  stamps: ['stamps', 'stamp-detail', 'sheets', 'userSheets'],
  rewards: ['rewards'],
  reward_groups: ['rewards'],
}

export const useTanstackCampaignFetchFn = (): FetchFn<CampaignGetResponse> => {
  const { campaignSlug } = useCampaignSlug()
  const locale = useQueryLocale()
  const { guest } = useApi()
  return {
    queryKey: buildLocaleQueryKey('campaign', campaignSlug, locale),
    staleTime: ONE_DAY_CACHE_TIME,
    queryFn: () => guest.fetchCampaign(),
  }
}

export const useTanstackSheetsFetchFn = (): FetchFn<SheetListResponse> => {
  const { campaignSlug } = useCampaignSlug()
  const locale = useQueryLocale()
  const { guest, auth } = useApi()
  const tokenLoading = useTokenStore((s) => s.loading)
  const idToken = useTokenStore((s) => s.idToken)
  const isAuthenticated = !tokenLoading && idToken !== null
  return {
    // 認証状態でキャッシュを分離（購入連動ロックの反映有無が変わるため）
    queryKey: buildLocaleQueryKey('sheets', campaignSlug, locale, isAuthenticated ? 'auth' : 'guest'),
    staleTime: ONE_DAY_CACHE_TIME,
    // 公開エンドポイント。ログイン時はトークンを送り購入連動ロック（COURSE_UNLOCK）を反映して
    // 解放済みシートのスタンプを取得。非ログイン時はゲストAPI（Authorization 無し）。
    queryFn: () => (isAuthenticated ? auth.fetchSheets() : guest.fetchSheets()),
  }
}

/**
 * 認証必須
 * staleTime: 0 — スタンプ獲得後に invalidateQueries(['userSheets']) を呼ぶことで即時更新する。
 */
export const useTanstackUserSheetsFetchFn = (): FetchFn<UserSheetListResponse> => {
  const { campaignSlug } = useCampaignSlug()
  const locale = useQueryLocale()
  const { auth } = useApi()
  return {
    queryKey: buildLocaleQueryKey('userSheets', campaignSlug, locale),
    staleTime: 0,
    queryFn: () => auth.fetchUserSheets(),
  }
}

export const useTanstackSpotsFetchFn = (): FetchFn<SpotListResponse> => {
  const { campaignSlug } = useCampaignSlug()
  const locale = useQueryLocale()
  const { guest, auth } = useApi()
  const tokenLoading = useTokenStore((s) => s.loading)
  const idToken = useTokenStore((s) => s.idToken)
  const isAuthenticated = !tokenLoading && idToken !== null
  return {
    // 認証状態でキャッシュを分離（購入連動ロックによるスポット除外の有無が変わるため）
    queryKey: buildLocaleQueryKey('spots', campaignSlug, locale, isAuthenticated ? 'auth' : 'guest'),
    staleTime: ONE_DAY_CACHE_TIME,
    // 公開エンドポイント。ログイン時はトークンを送り購入連動ロック（未購入シートのスポット除外）を反映。
    // 非ログイン時はゲストAPI（Authorization 無し）。
    queryFn: () => (isAuthenticated ? auth.fetchSpots() : guest.fetchSpots()),
  }
}

export const useTanstackSpotFetchFn = (spotId: number): FetchFn<SpotGetResponse> => {
  const { campaignSlug } = useCampaignSlug()
  const locale = useQueryLocale()
  const { guest, auth } = useApi()
  const tokenLoading = useTokenStore((s) => s.loading)
  const idToken = useTokenStore((s) => s.idToken)
  const isAuthenticated = !tokenLoading && idToken !== null
  return {
    // 認証状態でキャッシュを分離（未購入シートのみに紐づくスポットは未認証時 404 になるため）
    queryKey: buildLocaleQueryKey('spots', campaignSlug, locale, String(spotId), isAuthenticated ? 'auth' : 'guest'),
    staleTime: ONE_DAY_CACHE_TIME,
    queryFn: () => (isAuthenticated ? auth.fetchSpot(spotId) : guest.fetchSpot(spotId)),
  }
}

/**
 * GPS座標付きスポット取得APIのファクトリフック。
 * GPS座標は動的に決まるため、useStampQuizFetchFnFactory と同じ factory パターンを採用。
 * 返された factory を `queryClient.fetchQuery(factory(spotId, lat, lon))` で使用する。
 */
export const useTanstackSpotWithLatLonFetchFnFactory = (): ((
  spotId: number,
  latitude: number,
  longitude: number,
) => FetchFn<SpotGetResponse>) => {
  const { campaignSlug } = useCampaignSlug()
  const locale = useQueryLocale()
  const { guest, auth } = useApi()
  const tokenLoading = useTokenStore((s) => s.loading)
  const idToken = useTokenStore((s) => s.idToken)
  const isAuthenticated = !tokenLoading && idToken !== null
  return (spotId, latitude, longitude) => ({
    // 認証状態でキャッシュを分離（詳細と同じ購入連動アクセス制御が適用されるため）
    queryKey: buildLocaleQueryKey(
      'spots',
      campaignSlug,
      locale,
      String(spotId),
      String(latitude),
      String(longitude),
      isAuthenticated ? 'auth' : 'guest',
    ),
    // 60秒に深い意味はないが、短い期間に同じところから複数回実行された場合に同じ値を返すようにするため
    staleTime: 60 * 1000,
    queryFn: () =>
      isAuthenticated
        ? auth.fetchSpotWithLocation(spotId, latitude, longitude)
        : guest.fetchSpotWithLocation(spotId, latitude, longitude),
  })
}

export const useTanstackRewardsFetchFn = (): FetchFn<RewardListResponse> => {
  const { campaignSlug } = useCampaignSlug()
  const locale = useQueryLocale()
  const { guest } = useApi()
  return {
    queryKey: buildLocaleQueryKey('rewards', campaignSlug, locale),
    staleTime: ONE_DAY_CACHE_TIME,
    queryFn: () => guest.fetchRewards(),
  }
}

export const useTanstackRewardDetailFetchFn = (id: number): FetchFn<RewardGetResponse> => {
  const { campaignSlug } = useCampaignSlug()
  const locale = useQueryLocale()
  const { guest } = useApi()
  return {
    queryKey: buildLocaleQueryKey('rewards', campaignSlug, locale, String(id)),
    staleTime: ONE_DAY_CACHE_TIME,
    queryFn: () => guest.fetchReward(id),
  }
}

export const useTanstackStampDetailFetchFn = (stampId: number): FetchFn<StampGetResponse> => {
  const { campaignSlug } = useCampaignSlug()
  const locale = useQueryLocale()
  const { guest } = useApi()
  return {
    queryKey: buildLocaleQueryKey('stamp-detail', campaignSlug, locale, String(stampId)),
    staleTime: ONE_DAY_CACHE_TIME,
    queryFn: () => guest.fetchStamp(stampId),
  }
}

/**
 * 認証必須
 * staleTime: 0 — スタンプ獲得などで状況が変わる可能性があるため即時更新する。
 */
export const useTanstackUserRewardsFetchFn = (): FetchFn<UserRewardStatusListResponse> => {
  const { campaignSlug } = useCampaignSlug()
  const locale = useQueryLocale()
  const { auth } = useApi()
  return {
    queryKey: buildLocaleQueryKey('userRewards', campaignSlug, locale),
    staleTime: 0,
    queryFn: () => auth.fetchUserRewards(),
  }
}

/**
 * 認証必須
 * staleTime: 0 — 特典詳細の獲得状況は動的なため即時更新する。
 */
export const useTanstackUserRewardDetailFetchFn = (id: number): FetchFn<UserRewardStatusGetResponse> => {
  const { campaignSlug } = useCampaignSlug()
  const locale = useQueryLocale()
  const { auth } = useApi()
  return {
    queryKey: buildLocaleQueryKey('userRewards', campaignSlug, locale, String(id)),
    staleTime: 0,
    queryFn: () => auth.fetchUserReward(id),
  }
}

export const useTanstackMenuFetchFn = (): FetchFn<MenuListResponse> => {
  const { campaignSlug } = useCampaignSlug()
  const locale = useQueryLocale()
  const { guest } = useApi()
  return {
    queryKey: buildLocaleQueryKey('menus', campaignSlug, locale),
    staleTime: ONE_DAY_CACHE_TIME,
    queryFn: () => guest.fetchMenus(),
  }
}

/**
 * 認証済みユーザーのプロフィール取得（GET /api/v2/user）
 * 認証必須。enabled: isAuthenticated を付けて使用すること。
 * staleTime: 5分 — ユーザー情報は頻繁に変わらないが、完全にキャッシュし続けるのは避ける。
 */
export const useTanstackUserProfileFetchFn = (): FetchFn<UserGetResponse> => {
  const { campaignSlug } = useCampaignSlug()
  const { auth } = useApi()
  return {
    queryKey: ['userProfile', campaignSlug],
    staleTime: 5 * 60 * 1000, // 5分
    queryFn: () => auth.fetchUserProfile(),
  }
}

/**
 * お知らせ一覧の queryKey（#2973）。
 * 認証状態でキャッシュを分ける（ログイン / ログアウトで既読状態を混ぜない）。
 * 詳細ページで既読化したあとに一覧キャッシュを書き換えるためにも使う。
 */
export const buildNewsQueryKey = (campaignSlug: string, locale: string, isAuthenticated: boolean): string[] =>
  buildLocaleQueryKey('notifications', campaignSlug, locale, isAuthenticated ? 'auth' : 'guest')

/**
 * お知らせ一覧取得（GET /api/v2/notifications）
 * 認証任意。ログイン済みの場合は Authorization 付きで取得し、各 notification の isRead に
 * 会員の既読状態を反映させる（未認証時は API が全件 isRead: false を返す）（#2973）。
 */
export const useTanstackNewsFetchFn = (isAuthenticated: boolean): FetchFn<NotificationListResponse> => {
  const { campaignSlug } = useCampaignSlug()
  const locale = useQueryLocale()
  const { guest, auth } = useApi()
  return {
    queryKey: buildNewsQueryKey(campaignSlug, locale, isAuthenticated),
    staleTime: 5 * 60 * 1000, // 5分キャッシュ
    queryFn: isAuthenticated ? () => auth.fetchNotifications() : () => guest.fetchNotifications(),
  }
}

export const useTanstackNewsDetailFetchFn = (
  id: number,
  isAuthenticated: boolean,
): FetchFn<NotificationGetResponse> => {
  const { campaignSlug } = useCampaignSlug()
  const locale = useQueryLocale()
  const { guest, auth } = useApi()
  return {
    queryKey: buildLocaleQueryKey(
      'notifications',
      campaignSlug,
      locale,
      String(id),
      isAuthenticated ? 'auth' : 'guest',
    ),
    staleTime: 5 * 60 * 1000, // 5分キャッシュ
    // ログイン済みの場合は Authorizationヘッダ付きで取得し、サーバー側の既読処理を発火させる
    queryFn: isAuthenticated ? () => auth.fetchNotification(id) : () => guest.fetchNotification(id),
  }
}

/**
 * 認証必須
 * staleTime: 0 — 未読状態はお知らせ閲覧や通知受信で変化するため常に最新を取得する。
 */
export const useTanstackNotificationsUnreadFetchFn = (): FetchFn<NotificationsUnreadResponse> => {
  const { campaignSlug } = useCampaignSlug()
  const { auth } = useApi()
  return {
    queryKey: ['notificationsUnread', campaignSlug],
    staleTime: 0,
    queryFn: () => auth.fetchNotificationsUnread(),
  }
}

/**
 * アンケート一覧取得（GET /api/v2/questionnaires）
 * 認証不要
 * staleTime: 0 — answered（回答済みフラグ）はユーザー操作で変化するため、
 * 常に最新データを取得する。POST 後に invalidateQueries(['questionnaires']) を
 * 呼び出すことでも対応可能。
 */
export const useTanstackQuestionnairesFetchFn = (): FetchFn<QuestionnaireListResponse> => {
  const { campaignSlug } = useCampaignSlug()
  const locale = useQueryLocale()
  const { guest } = useApi()
  return {
    queryKey: buildLocaleQueryKey('questionnaires', campaignSlug, locale),
    staleTime: 0,
    queryFn: () => guest.fetchQuestionnaires(),
  }
}

/**
 * タイプ指定アンケートの queryKey を組み立てる（#3479）。
 *
 * rewardId ごとにキャッシュを分離するのが目的。未指定は 'all'（＝キャンペーン単位の従来どおりの解決）。
 * キーを分離しないと、特典 A のアンケートが特典 B の画面で staleTime 内にキャッシュヒットし、
 * API に rewardId を送っても「別特典のアンケートが出る」症状が react-query 側で再発する。
 *
 * 先頭 2 要素を ['questionnaires', 'type'] で固定しているため、
 * invalidateQueries(['questionnaires', 'type']) の前方一致は全 rewardId に効く。
 *
 * ユニットテストから検証するため export している。
 */
export const buildQuestionnairesTypeQueryKey = (
  type: 'register' | 'reward',
  campaignSlug: string,
  locale: string,
  rewardId?: number,
): string[] => ['questionnaires', 'type', type, campaignSlug, locale, rewardId != null ? String(rewardId) : 'all']

/**
 * タイプ指定アンケート取得（GET /api/v2/questionnaires/type/{type}）
 * 認証必須
 * staleTime: 0 — answered（回答済みフラグ）はユーザー操作で変化するため、
 * 常に最新データを取得する。POST 後に invalidateQueries(['questionnaires', 'type']) を
 * 呼び出すことでも対応可能。
 *
 * @param type     'register'（会員登録時）| 'reward'（特典取得時）
 * @param rewardId 対象特典 ID。type='reward' のときのみ有効で、指定すると
 *                 その特典に個別紐付けされたアンケートが優先的に解決される（#3479）。
 *                 未指定時はキャンペーン単位の解決（従来どおり）。
 */
export const useTanstackQuestionnairesTypeFetchFn = (
  type: 'register' | 'reward',
  rewardId?: number,
): FetchFn<QuestionnaireGetResponse> => {
  const { campaignSlug } = useCampaignSlug()
  const locale = useQueryLocale()
  const { auth } = useApi()
  // rewardId は type='reward' のときのみ意味を持ち、API 層も register では送信しない。
  // ここで一度正規化しておかないと「キーは分離されるがリクエストは同一」という
  // キーとリクエストの乖離が生まれ、同じ応答が別キーに二重キャッシュされる。
  const effectiveRewardId = type === 'reward' ? rewardId : undefined
  return {
    queryKey: buildQuestionnairesTypeQueryKey(type, campaignSlug, locale, effectiveRewardId),
    staleTime: 0,
    queryFn: () => auth.fetchQuestionnairesType(type, effectiveRewardId),
  }
}

/**
 * 認証不要
 * staleTime: 0 — answered（回答済みフラグ）はユーザー操作で変化するため、
 * 常に最新データを取得する。POST 後に invalidateQueries(['questionnaires']) を
 * 呼び出すことでも対応可能。
 */
export const useTanstackQuestionnaireFetchFn = (id: number): FetchFn<QuestionnaireGetResponse> => {
  const { campaignSlug } = useCampaignSlug()
  const locale = useQueryLocale()
  const { guest } = useApi()
  return {
    queryKey: buildLocaleQueryKey('questionnaires', campaignSlug, locale, String(id)),
    staleTime: 0,
    queryFn: () => guest.fetchQuestionnaire(id),
  }
}

/**
 * クイズ取得ファクトリー（GET /api/v2/stamps/{stampId}/quizzes）
 *
 * stampId はスポット取得後に確定するため、通常の `useTanstackXxxFetchFn(id)` パターンは使えない。
 * 代わりに「(stampId: number) => FetchFn<StampQuizListResponse>」を返す factory 関数を提供する。
 * 返された factory を Actor の input に渡し、
 * `queryClient.fetchQuery(factory(stampId))` で使用する。
 */
export const useStampQuizFetchFnFactory = (): ((stampId: number) => FetchFn<StampQuizListResponse>) => {
  const { campaignSlug } = useCampaignSlug()
  const locale = useQueryLocale()
  const { auth } = useApi()
  return (stampId: number): FetchFn<StampQuizListResponse> => ({
    queryKey: buildLocaleQueryKey('stamp-quiz', campaignSlug, locale, String(stampId)),
    staleTime: ONE_DAY_CACHE_TIME,
    queryFn: () => auth.fetchStampQuizzes(stampId),
  })
}

/**
 * 認証必須
 * staleTime: 1時間 — 更新頻度が低いため & スタンプ獲得時には invalidateQueries(['userStamps']) を
 * 呼ぶことで即時更新されるため、長めのキャッシュ時間を設定する。
 */
export const useTanstackUserStampsFetchFn = (): FetchFn<UserStampListResponse> => {
  const { campaignSlug } = useCampaignSlug()
  const locale = useQueryLocale()
  const { auth } = useApi()
  return {
    queryKey: buildLocaleQueryKey('userStamps', campaignSlug, locale),
    staleTime: 60 * 60 * 1000, // 1時間
    queryFn: () => auth.fetchUserStamps(),
  }
}
